import { Injectable } from "@angular/core";
import { EMPLOYEE_ROUTES } from "../shared/sidebar/employee-menu-item";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";

export interface RouteAccess {
  path: string;
  parentTitle: string;
  title: string;
  isAccessible: boolean;
}

@Injectable({
  providedIn: "root",
})
export class RouteAccessService {
  private authorizedRoutes: any[] = [];

  private routesSubject = new BehaviorSubject<any[]>(EMPLOYEE_ROUTES);
  private pathMapSubject = new BehaviorSubject<Map<string, RouteAccess>>(
    new Map()
  );

  routes$ = this.routesSubject.asObservable();
  pathMap$ = this.pathMapSubject.asObservable();

  constructor() {
    this.initializePathMap();
  }

  setAuthorizedRoutes(routes: any[]) {
    this.authorizedRoutes = routes;
  }

  getAuthorizedRoutes() {
    return this.authorizedRoutes;
  }

  /**
   * Initialize the path map for quick lookups
   */
  private initializePathMap(): void {
    const pathMap = new Map<string, RouteAccess>();

    EMPLOYEE_ROUTES.forEach((route: any) => {
      if (route.submenu && route.submenu.length) {
        route.submenu.forEach((submenu: any) => {
          const path = submenu.path.startsWith("/")
            ? submenu.path
            : `/${submenu.path}`;

          pathMap.set(path, {
            path,
            parentTitle: route.title,
            title: submenu.title,
            isAccessible: false, // Will be updated when filtering by permissions
          });
        });
      }
    });

    this.pathMapSubject.next(pathMap);
  }

  /**
   * Get all submenu paths from the routes
   */
  getAllSubmenuPaths(): string[] {
    const pathMap = this.pathMapSubject.value;
    return Array.from(pathMap.keys());
  }

  /**
   * Check if a path exists in the submenu paths
   */
  isValidSubmenuPath(path: string): boolean {
    return this.pathMapSubject.value.has(path);
  }

  /**
   * Get route information by path
   */
  getRouteByPath(path: string): RouteAccess | undefined {
    return this.pathMapSubject.value.get(path);
  }

  /**
   * Filter routes by user permissions
   */
  filterRoutesByPermission(): void {
    try {
      // Get user data from localStorage
      const raw = localStorage.getItem("Auth-user");
      if (!raw) {
        console.warn("Auth-user not found in localStorage");
        return;
      }

      const parsed = JSON.parse(raw);
      const permissions = parsed?.permissions || [];

      // If no permissions, return
      if (permissions.length === 0) {
        console.warn("No permissions found for user");
        return;
      }

      // Convert permission titles to lowercase for case-insensitive comparison
      const allowedTitles = permissions.map((p: any) => p.title.toLowerCase());

      // Update the path map with accessibility information
      const pathMap = this.pathMapSubject.value;

      pathMap.forEach((routeAccess, path) => {
        routeAccess.isAccessible = allowedTitles.includes(
          routeAccess.title.toLowerCase()
        );
      });

      this.pathMapSubject.next(pathMap);

      // Filter the routes for sidebar display
      const filteredRoutes = EMPLOYEE_ROUTES.map((route: any) => {
        // Filter submenu items
        const filteredSubmenu = (route.submenu || []).filter((sub: any) =>
          allowedTitles.includes(sub.title.toLowerCase())
        );

        // Check if parent route is allowed
        const isParentAllowed = allowedTitles.includes(
          route.title.toLowerCase()
        );

        // Include route if parent is allowed or it has allowed submenu items
        if (isParentAllowed || filteredSubmenu.length > 0) {
          return {
            ...route,
            submenu: filteredSubmenu,
          };
        }

        return null;
      }).filter((r: any) => r !== null);

      this.routesSubject.next(
        filteredRoutes.length > 0 ? filteredRoutes : EMPLOYEE_ROUTES
      );
    } catch (error) {
      console.error("Error filtering routes by permission:", error);
    }
  }

  /**
   * Get accessible paths for the current user
   */
  getAccessiblePaths(): Observable<string[]> {
    return this.pathMap$.pipe(
      map((pathMap) => {
        const accessiblePaths: string[] = [];
        pathMap.forEach((routeAccess, path) => {
          if (routeAccess.isAccessible) {
            accessiblePaths.push(path);
          }
        });
        return accessiblePaths;
      })
    );
  }
}
